import React, { createContext, useContext, useEffect, useState } from 'react';
import { AdminProfile } from '../types/admin';
import { adminApiClient } from '../api/client';

interface AdminAuthContextType {
  admin: AdminProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: React.ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state && window.location.pathname === '/admin/auth/google/callback') {
      const storedState = sessionStorage.getItem('oauth_state');
      sessionStorage.removeItem('oauth_state');
      if (storedState && storedState === state) {
        handleGoogleCallback(code, state);
      } else {
        setError('OAuth state mismatch. Please try logging in again.');
        setIsLoading(false);
      }
    }
  }, []);

  const initializeAuth = async () => {
    // Clean up legacy localStorage tokens from previous auth implementation
    localStorage.removeItem('admin_access_token');

    try {
      // Validate session via cookie — browser sends httpOnly cookie automatically
      const profile = await adminApiClient.getProfile();
      setAdmin(profile);
      localStorage.setItem('admin_user', JSON.stringify(profile));
    } catch {
      clearStoredAuth();
    }
    setIsLoading(false);
  };

  const handleGoogleCallback = async (code: string, state: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await adminApiClient.googleCallback(code, state);
      setAdmin(data.admin);
      localStorage.setItem('admin_user', JSON.stringify(data.admin));

      // Redirect to dashboard
      window.location.href = '/admin/dashboard';
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Authentication failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    try {
      setError(null);
      const { auth_url, state } = await adminApiClient.getGoogleLoginUrl();
      sessionStorage.setItem('oauth_state', state);
      window.location.href = auth_url;
    } catch (error: any) {
      setError(error.message || 'Failed to initiate login');
    }
  };

  const logout = async () => {
    try {
      await adminApiClient.logout();
    } catch {
      // Silently handle logout errors — cookie cleared server-side
    } finally {
      clearStoredAuth();
    }
  };

  const clearStoredAuth = () => {
    setAdmin(null);
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_user');
  };

  const clearError = () => {
    setError(null);
  };

  const value: AdminAuthContextType = {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    login,
    logout,
    error,
    clearError,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
